import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import { characters } from "@/mocks/characters";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "继母与继姐漫画角色详解",
    description:
      "继母与继姐漫画的主角介绍、继母角色、继姐角色人物设定详解，包含背景、性格与成长轨迹。",
    url: "/characters",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "/" },
      { "@type": "ListItem", position: 2, name: "角色详解", item: "/characters" },
    ],
  },
];

const colorMap: Record<string, { chip: string; line: string; dot: string }> = {
  primary: { chip: "bg-primary-100 text-primary-700", line: "border-primary-300", dot: "bg-primary-500" },
  accent: { chip: "bg-accent-100 text-accent-700", line: "border-accent-300", dot: "bg-accent-500" },
  secondary: { chip: "bg-secondary-100 text-secondary-700", line: "border-secondary-300", dot: "bg-secondary-500" },
};

export default function Characters() {
  return (
    <Layout>
      <Seo
        title="继母与继姐漫画角色详解 - 主角介绍/继母角色/继姐角色设定"
        description="继母与继姐漫画角色详解：主角介绍、继母角色、继姐角色的人物设定、性格解读与成长轨迹一次看全，帮你建立完整人物认知。"
        keywords="继母与继姐漫画角色,继母与继姐漫画主角介绍,继母与继姐漫画继母角色,继母与继姐漫画继姐角色,继母与继姐漫画角色设定"
        jsonLd={jsonLd}
      />
      {/* 页头 */}
      <section className="bg-gradient-to-b from-background-100 to-background-50 pt-24 md:pt-32">
        <div className="mx-auto max-w-6xl px-4 pb-10 md:px-6 md:pb-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">角色详解</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            继母与继姐漫画 · 角色详解
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
            从主角介绍，到继母角色、继姐角色的人物设定，逐一拆解每位核心人物的背景、性格与成长轨迹，
            帮你完整建立人物认知。
          </p>

          {/* 页内目录 */}
          <div className="mt-6 flex flex-wrap gap-2">
            {characters.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <span className={`h-2 w-2 rounded-full ${colorMap[c.color].dot}`}></span>
                {c.name} · {c.role}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 角色详情 */}
      {characters.map((c, idx) => {
        const cc = colorMap[c.color];
        return (
          <section
            key={c.id}
            id={c.id}
            className={`scroll-mt-24 py-12 md:py-16 ${idx % 2 === 1 ? "bg-background-100" : ""}`}
          >
            <div className="mx-auto max-w-6xl px-4 md:px-6">
              <article className={`grid items-start gap-8 lg:grid-cols-[400px_1fr] lg:gap-12 ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
                <div className={`overflow-hidden rounded-lg border border-background-200 ${idx % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                  <img
                    src={c.image}
                    alt={`继母与继姐漫画${c.role}${c.name}人物设定图`}
                    title={`${c.name} ${c.role}设定`}
                    className="aspect-[4/5] h-full w-full object-top object-cover"
                  />
                </div>

                <div className={idx % 2 === 1 ? "lg:[direction:ltr]" : ""}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`rounded-md px-3 py-1 text-xs font-medium ${cc.chip}`}>{c.role}</span>
                    <span className="text-xs tracking-widest text-foreground-500">{c.tagline}</span>
                  </div>
                  <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    {c.name}
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                    {c.summary}
                  </p>

                  {/* 人物档案 */}
                  <h3 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-focus-3-line text-primary-600"></i> 人物档案
                  </h3>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {c.keyFacts.map((f) => (
                      <div
                        key={f}
                        className={`rounded-lg border bg-background-50 p-4 ${cc.line}`}
                      >
                        <p className="text-sm leading-6 text-foreground-700">{f}</p>
                      </div>
                    ))}
                  </div>

                  <h3 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-user-smile-line text-primary-600"></i> 性格解读
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                    {c.personality}
                  </p>

                  <h3 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-git-branch-line text-primary-600"></i> 成长轨迹
                  </h3>
                  <ol className="mt-4 space-y-4 border-l-2 border-background-200 pl-5">
                    {c.growth.map((g, gi) => (
                      <li key={g} className="relative">
                        <span
                          className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ${cc.dot}`}
                        ></span>
                        <p className="text-sm leading-6 text-foreground-700">
                          <span className="mr-2 text-xs font-semibold text-foreground-400">
                            {String(gi + 1).padStart(2, "0")}
                          </span>
                          {g}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              </article>
            </div>
          </section>
        );
      })}

      {/* 底部导航 */}
      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6">
        <div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-background-100 p-6 sm:flex-row sm:items-center md:p-8">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">想理清三人的关系网？</h3>
            <p className="mt-2 text-sm text-foreground-600">
              从人物设定走向关系演变，看看他们如何在日常相处中彼此靠近。
            </p>
          </div>
          <Link
            to="/relationships"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看人物关系 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}